import { useEffect, useRef } from "react";
import { registrarFalhaTurnstile } from "@/lib/sentry-envio";

/**
 * Cloudflare Turnstile (modo Managed: invisível para quase todos). O token é de USO ÚNICO:
 * a tela muda `reiniciar` depois de cada chamada à API para receber outro.
 */
type ApiTurnstile = {
  render: (el: HTMLElement, o: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: ApiTurnstile;
  }
}

const SCRIPT =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let carregando: Promise<ApiTurnstile> | null = null;

function carregar(): Promise<ApiTurnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  carregando ??= new Promise((ok, erro) => {
    const s = document.createElement("script");
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => {
      if (window.turnstile) return ok(window.turnstile);
      carregando = null;
      erro(new Error("turnstile"));
    };
    s.onerror = () => {
      s.remove();
      carregando = null;
      erro(new Error("turnstile"));
    };
    document.head.appendChild(s);
  });
  return carregando;
}

export function Turnstile({
  onToken,
  onErro,
  reiniciar,
}: {
  onToken: (token: string | null) => void;
  /** Widget recusado (sitekey, erro do desafio) ou script bloqueado/fora do ar. */
  onErro?: () => void;
  reiniciar: number;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const id = useRef<string | null>(null);
  const aoToken = useRef(onToken);
  const aoErro = useRef(onErro);

  useEffect(() => {
    aoToken.current = onToken;
    aoErro.current = onErro;
  }, [onToken, onErro]);

  useEffect(() => {
    let ativo = true;
    carregar()
      .then((api) => {
        if (!ativo || !caixa.current || id.current) return;
        id.current = api.render(caixa.current, {
          sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "",
          language: "pt-br",
          // Invisível: só aparece se a Cloudflare precisar de um clique (acesso suspeito).
          appearance: "interaction-only",
          theme: "light",
          callback: (t: string) => aoToken.current(t),
          "expired-callback": () => aoToken.current(null),
          "error-callback": (codigo?: string) => {
            // Sem o captcha o funcionário não consegue se identificar (bloqueador, adblock, domínio).
            registrarFalhaTurnstile(codigo);
            aoToken.current(null);
            aoErro.current?.();
          },
        });
      })
      .catch(() => {
        if (!ativo) return;
        // Script da Cloudflare não carregou (bloqueador, rede): sem captcha não há identificação.
        registrarFalhaTurnstile("script");
        aoToken.current(null);
        aoErro.current?.();
      });
    return () => {
      ativo = false;
      if (id.current) window.turnstile?.remove(id.current);
      id.current = null;
    };
  }, []);

  useEffect(() => {
    if (reiniciar === 0 || !id.current) return;
    aoToken.current(null);
    window.turnstile?.reset(id.current);
  }, [reiniciar]);

  return <div ref={caixa} className="flex justify-center empty:hidden" />;
}
